import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, RefreshCw, RotateCcw, Image as ImageIcon } from 'lucide-react';

// ── Passport-style head-and-shoulders capture ────────────────────────────────
// Primary path is a live in-app camera (getUserMedia) with a portrait framing
// guide. If the camera can't be opened (permission denied, unsupported, http),
// it falls back to the native file picker — which on phones still offers
// "Take Photo" — so an applicant photo can always be captured/attached.
// Output is a compressed JPEG data URL (max 720px long edge) to stay well under
// the serverless body limit.
//
// EVERY STEP IS TIMED OUT AND EVERY FAILURE IS SPOKEN. A driver's phone that
// returns a photo the canvas can't re-encode used to look like a button that
// did nothing: createImageBitmap and toBlob/toDataURL can HANG (never resolve,
// never throw) when the device's graphics stack is wedged, so `busy` stayed on
// and no error was ever set. GALLEGUIL lost two days of onboard scans and could
// not close a shift because of it. Nothing here may hang, and if the re-encode
// can't be done at all the ORIGINAL JPEG is sent instead of nothing.

// No single step gets longer than this. A hung decode is indistinguishable from
// a slow one to the driver, and after 8s it is a fault either way.
const STEP_TIMEOUT_MS = 8000;
// Last-resort "send the camera's own JPEG untouched" ceiling. Netlify's function
// body limit is 6 MB and base64 inflates ~37%, so 3.5 MB of file leaves room for
// the JSON around it.
const RAW_MAX_BYTES = 3_500_000;

function withTimeout(promise, label, ms = STEP_TIMEOUT_MS) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(label + ' timed out')), ms); }),
  ]).finally(() => clearTimeout(timer));
}

// A canvas that has run out of memory doesn't throw — it hands back the string
// "data:," and lets the caller upload nothing. Treat anything that isn't a real
// JPEG payload as a failure so the fallbacks get their turn.
function assertJpegDataUrl(url) {
  if (typeof url !== 'string' || !/^data:image\/jpeg;base64,/.test(url) || url.length < 512) {
    throw new Error('the image came back empty');
  }
  return url;
}

function frameToDataUrl(source, sw, sh, maxEdge = 720, quality = 0.82) {
  const ratio = Math.min(1, maxEdge / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * ratio));
  const h = Math.max(1, Math.round(sh * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('this phone could not open a drawing canvas');
  ctx.drawImage(source, 0, 0, w, h);
  return assertJpegDataUrl(canvas.toDataURL('image/jpeg', quality));
}

// Route 1 — createImageBitmap. Fastest, and the only one that bakes in the EXIF
// orientation an iPhone leaves on a portrait shot.
async function bitmapRoute(file, maxEdge, quality) {
  if (typeof createImageBitmap !== 'function') throw new Error('no createImageBitmap');
  const bitmap = await withTimeout(
    createImageBitmap(file, { imageOrientation: 'from-image' }), 'decoding the photo');
  try { return frameToDataUrl(bitmap, bitmap.width, bitmap.height, maxEdge, quality); }
  finally { if (bitmap.close) bitmap.close(); }
}

// Route 2 — FileReader + <img>. Works on browsers whose createImageBitmap either
// rejects the imageOrientation option or never answers.
function imgRoute(file, maxEdge, quality) {
  return withTimeout(new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try { resolve(frameToDataUrl(img, img.naturalWidth || img.width, img.naturalHeight || img.height, maxEdge, quality)); }
        catch (e) { reject(e); }
      };
      img.onerror = () => reject(new Error('this phone could not open that image'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('the photo could not be read off the phone'));
    reader.readAsDataURL(file);
  }), 'reading the photo');
}

// Route 3 — send the camera's own bytes, no canvas involved. Only for JPEGs small
// enough to post; it is the difference between an unshrunk photo and no photo.
function rawRoute(file) {
  if (file.size > RAW_MAX_BYTES) throw new Error('the photo is too big to send unshrunk');
  return withTimeout(new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      // Normalise image/jpg → image/jpeg: every caller strips the prefix and
      // labels the upload image/jpeg, so the payload must genuinely be one.
      const m = /^data:image\/jpe?g;base64,/i.exec(url);
      if (!m) return reject(new Error('the phone sent a format we cannot use'));
      resolve('data:image/jpeg;base64,' + url.slice(m[0].length));
    };
    reader.onerror = () => reject(new Error('the photo could not be read off the phone'));
    reader.readAsDataURL(file);
  }), 'reading the photo');
}

// Try each route in turn. The last error is the one the driver is shown, because
// by then it is the only thing left that went wrong.
async function fileToDataUrl(file, maxEdge = 720, quality = 0.82) {
  const attempts = [
    () => bitmapRoute(file, maxEdge, quality),
    () => imgRoute(file, maxEdge, quality),
    () => rawRoute(file),
  ];
  let last;
  for (const attempt of attempts) {
    try { return await attempt(); }
    catch (e) { last = e; }
  }
  throw last || new Error('the photo could not be prepared');
}

// onFailure(message) fires whenever a capture ATTEMPT fails, so a screen that
// requires a photo can offer a way past a phone that cannot take one. It is only
// ever called after a real attempt — never for a driver who simply hasn't tried.
export default function CameraCapture({ value, onCapture, onClear, maxEdge = 720, quality = 0.82, guide = 'portrait', nativeFirst = false, onFailure }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const libRef = useRef(null);
  const [facing, setFacing] = useState('environment'); // rear by default (interviewer photographing applicant/docs)
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fail = useCallback((message) => {
    setError(message);
    if (onFailure) onFailure(message);
  }, [onFailure]);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setLive(false);
  }, []);

  // Callback ref: bind the stream the instant the <video> node mounts. This
  // avoids the race where the <video> (only rendered while `live`) isn't yet in
  // the DOM when getUserMedia resolves — a one-shot rAF could fire too early and
  // leave the camera on but the preview black (worst on the Retake path, where
  // the preview branch is still rendered when start() runs).
  const attachVideo = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      try {
        node.srcObject = streamRef.current;
        node.play().catch(() => {});
      } catch (e) { /* ignore */ }
    }
  }, []);

  const start = useCallback(async (mode) => {
    setError('');
    const want = mode || facing;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Live camera isn’t available here — use “Take / choose photo”.');
      return;
    }
    setBusy(true);
    try {
      stop();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: want }, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      // The attachVideo callback ref binds the stream as soon as the <video>
      // node commits to the DOM (handles the value/live branch timing safely).
      setLive(true);
    } catch (e) {
      setError('Couldn’t open the camera (' + (e.name || 'error') + '). Use “Take / choose photo”.');
      setLive(false);
    } finally {
      setBusy(false);
    }
  }, [facing, stop]);

  // Release the camera when the component unmounts.
  useEffect(() => () => stop(), [stop]);

  // Never a silent no-op: a Capture press that lands before the preview has a
  // frame used to do literally nothing, which reads as a broken button.
  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) {
      fail('The preview hasn’t started yet — give it a second and tap Capture again.');
      return;
    }
    let url;
    try { url = frameToDataUrl(v, v.videoWidth, v.videoHeight, maxEdge, quality); }
    catch (e) {
      fail('Couldn’t save that frame (' + e.message + '). Use “Take / choose photo” instead.');
      return;
    }
    stop();
    onCapture(url);
  };

  const switchCamera = () => {
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    start(next);
  };

  const onFile = async (e) => {
    const input = e.target;
    const f = input.files && input.files[0];
    // A camera app that is killed mid-shot — routine on an older Android under
    // memory pressure — fires change with an EMPTY file list. Saying nothing
    // there is what "I pressed the button and nothing happened" was.
    if (!f) {
      if (input) input.value = '';
      fail('Your phone came back without a photo. Take it again, or take it in your own Camera app and use “Upload / screenshot”.');
      return;
    }
    if (!f.size) {
      if (input) input.value = '';
      fail('That photo came through empty. Take it again, or use “Upload / screenshot”.');
      return;
    }
    setBusy(true);
    setError('');
    try { onCapture(await fileToDataUrl(f, maxEdge, quality)); }
    catch (err) {
      fail('Couldn’t prepare that photo — ' + err.message +
        '. Take it in your own Camera app and use “Upload / screenshot”, or tell the office.');
    }
    finally { setBusy(false); if (input) input.value = ''; }
  };

  const isDoc = guide === 'document';
  const frameBox = {
    position: 'relative', width: '100%', aspectRatio: isDoc ? '4 / 3' : '3 / 4', maxHeight: isDoc ? 300 : 360,
    background: '#000', borderRadius: 10, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid var(--hp-border)',
  };

  // ── Captured / existing photo preview ──
  if (value) {
    return (
      <div>
        <div style={frameBox}>
          <img src={value} alt="Applicant" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { onClear && onClear(); if (!nativeFirst) start(); }}>
            <RotateCcw size={13} /> Retake
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { onClear && onClear(); }}>
            Remove
          </button>
        </div>
      </div>
    );
  }

  // ── Live camera ──
  if (live) {
    return (
      <div>
        <div style={frameBox}>
          <video ref={attachVideo} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {/* Framing guide */}
          {guide !== 'none' && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <div style={isDoc
                ? { position: 'absolute', top: '14%', left: '7%', right: '7%', bottom: '14%', border: '2px dashed rgba(255,255,255,0.85)', borderRadius: 8 }
                : { position: 'absolute', top: '12%', left: '22%', right: '22%', bottom: '8%', border: '2px dashed rgba(255,255,255,0.85)', borderRadius: '50% 50% 45% 45%' }
              } />
              <div style={{
                position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center',
                color: '#fff', fontSize: '.7rem', textShadow: '0 1px 3px rgba(0,0,0,.8)',
              }}>
                {isDoc ? 'Fit the whole document in the frame' : 'Fit head & shoulders inside the frame'}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={capture}>
            <Camera size={13} /> Capture
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={switchCamera}>
            <RefreshCw size={13} /> Flip camera
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={stop}>Cancel</button>
        </div>
      </div>
    );
  }

  // ── Idle / fallback ──
  return (
    <div>
      {/* "Preparing" is drawn, not implied. Disabling the buttons and saying
          nothing is what a hung decode looked like from the driver's seat. */}
      <div style={{ ...frameBox, background: 'var(--hp-surface)', flexDirection: 'column', gap: 8, color: 'var(--hp-dim)' }}>
        {busy ? <RefreshCw size={30} style={{ opacity: 0.7, animation: 'spin 0.9s linear infinite' }} />
              : <Camera size={30} style={{ opacity: 0.5 }} />}
        <span style={{ fontSize: '.75rem' }}>{busy ? 'Preparing your photo…' : 'No photo yet'}</span>
      </div>
      {/* Native camera — opens the phone's Camera app, where the physical side /
          volume buttons act as the shutter. getUserMedia (live preview) can't do that. */}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} style={{ display: 'none' }} />
      {/* Library / screenshot — no `capture`, so it offers the photo library & Files. */}
      <input ref={libRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
      {nativeFirst ? (
        <div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>
              <Camera size={13} /> {busy ? 'Preparing…' : 'Take photo'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => libRef.current && libRef.current.click()} disabled={busy}>
              <ImageIcon size={13} /> Upload / screenshot
            </button>
          </div>
          <button type="button" onClick={() => start()} disabled={busy}
            style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, color: 'var(--hp-dim)', fontSize: '.72rem', textDecoration: 'underline', cursor: 'pointer' }}>
            {busy ? 'Working…' : 'Use live camera instead (no side-button capture)'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => start()} disabled={busy}>
            <Camera size={13} /> {busy ? 'Opening…' : 'Open camera'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>
            <ImageIcon size={13} /> Take / choose photo
          </button>
        </div>
      )}
      {error && <div style={{ fontSize: '.75rem', color: 'var(--hp-amber)', marginTop: 8, lineHeight: 1.5 }}>{error}</div>}
    </div>
  );
}
